import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { continueRender, delayRender } from "remotion";
import { asset } from "./assets";
import { PICK_CARDS, RECTS, SCREEN_CSS, SCREENS } from "./screens.generated";

// Windows onto the settled booking artboards (390 x 844): each card in the video is a crop of the
// real design markup, not a redrawing of it.
export const SW = 390;
export const SH = 844;

// the sheets' own stylesheet (fonts and weight classes), scoped to the screens
export const SCOPED_CSS = SCREEN_CSS.replace(/body\s*\{/, "[data-scr]{")
	.replace(/\n\s*(\.[a-z]+ \{)/g, "\n[data-scr] $1")
	.replace(/\} (\.[a-z]+ \{)/g, "} [data-scr] $1")
	.replace(/\bp \{/, "[data-scr] p {")
	.replace(/\ba \{/, "[data-scr] a {");

const withAssets = (html: string) => html.replace(/src="([^"]+)"/g, (_m, p) => `src="${asset("screens/" + p)}"`);

// Pictures inside the artboard markup are not <Img>, so Remotion would not wait for them: preload once.
let preloaded = false;
export const usePreloadScreens = () => {
	const [handle] = useState(() => (preloaded ? null : delayRender("screen pictures")));
	React.useEffect(() => {
		if (!handle) return;
		const urls = new Set<string>();
		for (const h of [...Object.values(SCREENS), ...PICK_CARDS]) {
			for (const m of h.matchAll(/src="([^"]+)"/g)) urls.add(asset("screens/" + m[1]));
		}
		Promise.all(
			[...urls].map(
				(u) =>
					new Promise<void>((res) => {
						const im = new Image();
						im.onload = () => res();
						im.onerror = () => res();
						im.src = u;
					})
			)
		).then(() => {
			preloaded = true;
			continueRender(handle);
		});
	}, [handle]);
};

// Remotion waits for its own <Img>, not for <img> inside injected markup, and Chrome decodes
// pictures lazily at paint time: without this, a frame can be captured before a photo is
// decoded and the picture blinks. Every render waits until every picture in the box is decoded.
export const useDecodedImages = (ref: React.RefObject<HTMLElement | null>) => {
	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		const imgs = [...el.querySelectorAll("img")];
		if (!imgs.length) return;
		const handle = delayRender("decode pictures");
		Promise.all(
			imgs.map((im) =>
				(im.complete && im.naturalWidth > 0
					? Promise.resolve()
					: new Promise<void>((res) => {
							im.addEventListener("load", () => res(), { once: true });
							im.addEventListener("error", () => res(), { once: true });
						})
				).then(() => im.decode().catch(() => undefined))
			)
		).then(() => continueRender(handle));
	});
};

// data-k -> css declarations for this frame; a key starting with ">" is a raw selector inside the screen
export type Anim = Record<string, string>;

// One settled artboard, exactly as drawn. `anim` styles tagged parts per frame.
export const Screen: React.FC<{ code: string; anim?: Anim; id: string; extraCss?: string }> = ({ code, anim, id, extraCss = "" }) => {
	const html = useMemo(() => withAssets(SCREENS[code]), [code]);
	const css = anim
		? Object.entries(anim)
				.map(([k, v]) => (k.startsWith(">") ? `[data-scr="${id}"] ${k.slice(1)}{${v}}` : `[data-scr="${id}"] [data-k="${k}"]{${v}}`))
				.join("\n")
		: "";
	const ref = useRef<HTMLDivElement>(null);
	useDecodedImages(ref);
	return (
		<div ref={ref} data-scr={id} style={{ position: "absolute", inset: 0 }}>
			<style>{`${SCOPED_CSS}\n[data-scr="${id}"] > div > div:first-child{border:none!important;box-shadow:none!important;border-radius:0!important}\n${extraCss}\n${css}`}</style>
			<div dangerouslySetInnerHTML={{ __html: html }} />
		</div>
	);
};

export const PickCard: React.FC<{ i: number }> = ({ i }) => {
	const html = useMemo(() => withAssets(PICK_CARDS[i]), [i]);
	const ref = useRef<HTMLDivElement>(null);
	useDecodedImages(ref);
	return <div ref={ref} data-scr={`pick${i}`} dangerouslySetInnerHTML={{ __html: html }} />;
};

export const rect = (code: string, k: string) => RECTS[code][k];

export type Rect = { x: number; y: number; w: number; h: number };

// A window onto one settled artboard: shows `r` (artboard px) at 1:1, clipped with the card's radius.
export const Crop: React.FC<{ code: string; r: Rect; id: string; anim?: Anim; radius?: number; shadow?: boolean; transparent?: boolean }> = ({
	code,
	r,
	id,
	anim,
	radius = 20,
	shadow = true,
	transparent = false,
}) => (
	<div
		style={{
			position: "relative",
			width: r.w,
			height: r.h,
			borderRadius: radius,
			overflow: "hidden",
			background: transparent ? "transparent" : "#fff",
			boxShadow: shadow ? "0 10px 30px rgba(15,62,74,.10), 0 2px 6px rgba(15,62,74,.05)" : undefined,
		}}
	>
		<div style={{ position: "absolute", left: -r.x, top: -r.y, width: SW, height: SH }}>
			<Screen code={code} anim={anim} id={id} extraCss={transparent ? `[data-scr="${id}"] > div > div:first-child{background:transparent!important}` : ""} />
		</div>
	</div>
);
